/**
 * Incident Entity Schemas
 *
 * Fire-drill framework POC. Type-extensible incident model — only
 * FIRE_DRILL is exercised today; lockdown / closure flavors layer on
 * top by extending the type enum.
 *
 * Shapes mirror apps/backend/prisma/schema/incident.prisma. IDs are
 * the Prisma `publicId` (CUID), not the internal BigInt.
 */

import { z } from 'zod'

export const IncidentTypeSchema = z.enum(['FIRE_DRILL'])
export type IncidentType = z.infer<typeof IncidentTypeSchema>

export const IncidentStatusSchema = z.enum(['SCHEDULED', 'ACTIVE', 'RESOLVED'])
export type IncidentStatus = z.infer<typeof IncidentStatusSchema>

export const ParticipantStatusSchema = z.enum(['UNACCOUNTED', 'CONFIRMED_PRESENT', 'MISSING'])
export type ParticipantStatus = z.infer<typeof ParticipantStatusSchema>

export const ClaimTypeSchema = z.enum(['STAFF', 'PARENT', 'STUDENT', 'SELF'])
export type ClaimType = z.infer<typeof ClaimTypeSchema>

export const ClaimStatusSchema = z.enum(['AUTO_APPROVED', 'PENDING', 'CONFIRMED', 'REJECTED'])
export type ClaimStatus = z.infer<typeof ClaimStatusSchema>

/**
 * Embedded reference to a school user. Used for actor attribution
 * (claimedBy, confirmedBy, startedBy, resolvedBy) and for participant
 * identity on the dashboard.
 */
export const IncidentSchoolUserRefSchema = z.object({
  id: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  profileImageUrl: z.string().nullable().optional()
})
export type IncidentSchoolUserRef = z.infer<typeof IncidentSchoolUserRefSchema>

/**
 * Resolve-time summary, populated when the incident moves to RESOLVED.
 */
export const IncidentSummarySchema = z.object({
  totalParticipants: z.number(),
  totalConfirmedPresent: z.number(),
  totalUnaccounted: z.number(),
  totalMissing: z.number(),
  claimCounts: z.object({
    staff: z.number(),
    parent: z.number(),
    student: z.number(),
    self: z.number()
  }),
  durationMs: z.number()
})
export type IncidentSummary = z.infer<typeof IncidentSummarySchema>

export const IncidentClaimSchema = z.object({
  id: z.string(),
  incidentId: z.string(),
  participantId: z.string(),
  claimType: ClaimTypeSchema,
  status: ClaimStatusSchema,
  claimedBy: IncidentSchoolUserRefSchema.nullable(),
  confirmedBy: IncidentSchoolUserRefSchema.nullable(),
  confirmedAt: z.string().nullable(),
  note: z.string().nullable(),
  createdAt: z.string()
})
export type IncidentClaim = z.infer<typeof IncidentClaimSchema>

export const IncidentParticipantSchema = z.object({
  id: z.string(),
  incidentId: z.string(),
  schoolUser: IncidentSchoolUserRefSchema,
  status: ParticipantStatusSchema,
  // Most recent confirmed claim (denormalized for dashboard render).
  confirmedClaim: IncidentClaimSchema.nullable(),
  createdAt: z.string(),
  updatedAt: z.string().nullable()
})
export type IncidentParticipant = z.infer<typeof IncidentParticipantSchema>

export const IncidentSchema = z.object({
  id: z.string(),
  schoolId: z.string(),
  type: IncidentTypeSchema,
  status: IncidentStatusSchema,
  scheduledAt: z.string().nullable(),
  startedAt: z.string().nullable(),
  resolvedAt: z.string().nullable(),
  startedBy: IncidentSchoolUserRefSchema,
  resolvedBy: IncidentSchoolUserRefSchema.nullable(),
  summary: IncidentSummarySchema.nullable(),
  createdAt: z.string(),
  updatedAt: z.string().nullable()
})
export type Incident = z.infer<typeof IncidentSchema>

/**
 * Full snapshot used by the dashboard / detail screen. The participants
 * array is the denormalized "where is everyone" projection; pendingClaims
 * is the queue of parent/student claims awaiting teacher confirmation.
 */
export const IncidentSnapshotSchema = IncidentSchema.extend({
  participants: z.array(IncidentParticipantSchema),
  pendingClaims: z.array(IncidentClaimSchema),
  totals: z.object({
    participants: z.number(),
    confirmedPresent: z.number(),
    unaccounted: z.number(),
    missing: z.number(),
    pendingClaims: z.number(),
    percentAccounted: z.number()
  })
})
export type IncidentSnapshot = z.infer<typeof IncidentSnapshotSchema>
